import type { RootStore } from '~/stores/root.store';
import type { GlobalCXContextValue } from '~/components/ClientExperience/providers/global-context/global-cx.context';
import type { GlobalMXContextValue } from '~/providers/mx-global-context/global-mx.context';
import type { CXRootStore } from '~/components/ClientExperience/stores/cx-root.store';

export enum Direction {
    Top = 'top',
    Bottom = 'bottom',
    Left = 'left',
    Right = 'right'
}

export interface IDimensions {
    width: number;
    height: number;
}

export interface IPoint {
    x: number;
    y: number;
}

export type TRootStore = RootStore | CXRootStore;
export type TGlobalContext = GlobalCXContextValue | GlobalMXContextValue;

export type noop = () => void;

export type Rename<T, K extends keyof T, R extends string> = Omit<T, K> & {
    [P in R]: T[K];
};

/** Extract from an interface only those keys that are typed as the given type parameter. */
export type KeysMatching<T extends Record<string, any>, V> = {
    [K in keyof T]-?: T[K] extends V ? K : never;
}[keyof T];

/** Extract from an interface only those keys that are typed as strings. */
export type StringKeys<T extends Record<string, any>> = KeysMatching<T, string>;

/** Extract from an interface only those keys that are typed as numbers. */
export type NumberKeys<T extends Record<string, any>> = KeysMatching<T, number>;

/** Extract from an interface only those keys that are typed as numbers. */
export type BooleanKeys<T extends Record<string, any>> = KeysMatching<
    T,
    boolean
>;

type IsSingleKeyObjectOfType<T, K> = K extends Record<string, T>
    ? TuplifyUnion<keyof K>['length'] extends 1
        ? true
        : false
    : false;

export type IsSingleKeyBooleanObject<T> = IsSingleKeyObjectOfType<boolean, T>;

/** copyright ChatGPT */
export type ExcludeKeysOfType<T extends Record<string, any>, I> = Exclude<
    { [K in keyof T]: T[K] extends I ? never : K }[keyof T],
    never
>;

/** Creates a new object type by picking only the properties from T whose values do NOT extend V.*/
export type PropsExcludingType<T, V> = {
    [K in keyof T as T[K] extends V ? never : K]: T[K];
};

export type ExcludeFalseKeys<T extends Record<string, any>> = ExcludeKeysOfType<
    T,
    false
>;

export type ExcludeNeverKeys<T extends Record<string, any>> = ExcludeKeysOfType<
    T,
    never
>;

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (
    k: infer I
) => void
    ? I
    : never;

// https://stackoverflow.com/questions/55127004/how-to-transform-union-type-to-tuple-type
export type TuplifyUnion<
    T,
    L = LastOf<T>,
    N = [T] extends [never] ? true : false
> = true extends N ? [] : Push<TuplifyUnion<Exclude<T, L>>, L>;

type LastOf<T> = UnionToIntersection<
    T extends any ? () => T : never
> extends () => infer R
    ? R
    : never;

type Push<T extends any[], V> = [...T, V];

// https://stackoverflow.com/questions/58434389/typescript-deep-keyof-of-a-nested-object
export type Leaves<T, D extends number = 10> = [D] extends [never]
    ? never
    : T extends object
    ? { [K in keyof T]-?: Join<K, Leaves<T[K], Prev[D]>> }[keyof T]
    : '';

export type Paths<T, D extends number = 10> = [D] extends [never]
    ? never
    : T extends object
    ? {
          [K in keyof T]-?: K extends string | number
              ? `${K}` | Join<K, Paths<T[K], Prev[D]>>
              : never;
      }[keyof T]
    : '';

type Join<K, P> = K extends string | number
    ? P extends string | number
        ? `${K}${'' extends P ? '' : '.'}${P}`
        : never
    : never;

type Prev = [
    never,
    0,
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    11,
    12,
    13,
    14,
    15,
    16,
    17,
    18,
    19,
    20,
    ...0[]
];

export type DeepValue<
    Obj extends Record<string, any>,
    ChainedKey extends Leaves<Obj> | Paths<Obj>
> = ChainedKey extends `${infer A}.${infer B}`
    ? // @ts-ignore
      DeepValue<Obj[A], B>
    : Obj[ChainedKey];

export type PromiseType<T extends Promise<any>> = T extends Promise<infer R>
    ? R
    : never;
export type Enumify<E> = E extends Record<any, infer T> ? T : never;

export type TDayOfWeek =
    | 'Sunday'
    | 'Monday'
    | 'Tuesday'
    | 'Wednesday'
    | 'Thursday'
    | 'Friday'
    | 'Saturday';

export type Nullable<T> = T | null;

export type PartialWithRequired<T, K extends keyof T> = Partial<Omit<T, K>> &
    Required<Pick<T, K>>;

/** Extract from an interface only those keys that have the given return value. */
export type KeysWithReturnType<T, R> = {
    [K in keyof T]: T[K] extends (...args: any[]) => R ? K : never;
}[keyof T];

/** Destruct the first item type in the array out and keep the rest */
export type Tail<T extends any[]> = T extends [T[0], ...infer Rest] ? Rest : [];

export type MakeOptional<T, K extends keyof T> = Omit<T, K> &
    Partial<Pick<T, K>>;

/**
 * Enforces that the provided keys are valid keys of the given object.
 * If any key is invalid, TypeScript will produce a compile-time error.
 */
export type EnforceValidKeys<TObj, TKeys extends keyof TObj> = TKeys;
